import React, { useCallback, useEffect } from 'react';
import PatientInfo from './PatientInfo';
import { useUltrasoundForm } from '../hooks/useUltrasoundForm';
import DraftRestoreBanner from './DraftRestoreBanner';
import { useTechnologists } from '../hooks/useTechnologists';

const ACCESS_TYPE_OPTIONS = ['Native Fistula', 'Synthetic Graft'];

const GRAFT_MATERIAL_OPTIONS = ['PTFE', 'Dacron', 'Bovine', 'Other'];

const CONFIGURATION_OPTIONS = [
  'Radiocephalic',
  'Brachiocephalic',
  'Brachiobasilic',
  'Upper Arm Loop Graft',
  'Forearm Loop Graft',
  'Other'
];

const INDICATION_OPTIONS = [
  'Maturation Assessment',
  'Surveillance',
  'Malfunction',
  'Low Flow on Dialysis',
  'Other'
];

const PATENCY_OPTIONS = ['Patent', 'Thrombosed', 'Partially Thrombosed'];

const WAVEFORM_OPTIONS = ['Triphasic', 'Biphasic', 'Monophasic', 'High Resistance', 'Low Resistance'];

const STENOSIS_SEVERITY_OPTIONS = ['<50%', '50-75%', '>75%', 'Occlusion'];

const ANEURYSM_TYPE_OPTIONS = ['Aneurysm', 'Pseudoaneurysm'];

const PERIGRAFT_FLUID_TYPE_OPTIONS = ['Fluid Collection', 'Hematoma', 'Seroma'];

const TABS = ['Access & Patency', 'Inflow & Anastomosis', 'Body & Outflow', 'Complications'];

const INITIAL_DIALYSIS_ACCESS_DATA = {
  examType: 'Dialysis Access',
  examDate: new Date().toISOString().split('T')[0],
  firstName: '',
  lastName: '',
  mrn: '',
  technologist: '',
  reason: '',
  callReport: false,
  phoneNumber: '',
  history: '',
  comments: '',

  accessType: 'Native Fistula',
  graftMaterial: 'PTFE',
  graftMaterialOther: '',
  configuration: 'Radiocephalic',
  configurationOther: '',
  arm: 'Left',
  indication: 'Surveillance',
  indicationOther: '',

  patency: 'Patent',

  inflow: {
    psv: '',
    waveform: 'Triphasic',
    stenosisPresent: false,
    stenosisPSV: ''
  },

  anastomosis: {
    psv: '',
    diameter: ''
  },

  body: {
    proximalDiameter: '',
    proximalPSV: '',
    midDiameter: '',
    midPSV: '',
    distalDiameter: '',
    distalPSV: '',
    wallThickening: false,
    calcification: false,
    muralThrombus: false
  },

  outflow: {
    psv: '',
    stenosisPresent: false,
    stenosisLocation: '',
    stenosisPSV: ''
  },

  complications: {
    stenoses: [],
    thrombi: [],
    aneurysms: [],
    perigraftFluidPresent: false,
    perigraftFluidType: 'Fluid Collection',
    perigraftFluidDescription: ''
  }
};

function DialysisAccessForm({ token, onBack, facilityInfo }) {
  const [activeTab, setActiveTab] = React.useState(0);
  const { technologists, defaultTech } = useTechnologists();

  const {
    formData,
    updateField,
    updateNestedField,
    handleSubmit: hookHandleSubmit,
    handlePreview,
    resetForm,
    isLoading,
    error,
    success,
    clearMessages,
    fieldErrors,
    draftBannerProps
  } = useUltrasoundForm(
    { ...INITIAL_DIALYSIS_ACCESS_DATA, technologist: defaultTech },
    'Dialysis Access',
    { token, facilityInfo }
  );

  useEffect(() => {
    if (defaultTech && !formData.technologist) {
      updateField('technologist', defaultTech);
    }
  }, [defaultTech, formData.technologist, updateField]);

  // Generalized add/remove/update helpers for the three complication arrays
  const addComplicationEntry = useCallback((field, newItem) => {
    updateNestedField(`complications.${field}`, [...formData.complications[field], newItem]);
  }, [formData, updateNestedField]);

  const removeComplicationEntry = useCallback((field, index) => {
    updateNestedField(`complications.${field}`, formData.complications[field].filter((_, i) => i !== index));
  }, [formData, updateNestedField]);

  const updateComplicationEntry = useCallback((field, index, key, value) => {
    const updated = formData.complications[field].map((item, i) => i === index ? { ...item, [key]: value } : item);
    updateNestedField(`complications.${field}`, updated);
  }, [formData, updateNestedField]);

  const addStenosis = useCallback(() => {
    addComplicationEntry('stenoses', { location: '', severity: '<50%', grayscale: '' });
  }, [addComplicationEntry]);

  const addThrombus = useCallback(() => {
    addComplicationEntry('thrombi', { location: '', occlusive: 'Non-occlusive' });
  }, [addComplicationEntry]);

  const addAneurysm = useCallback(() => {
    addComplicationEntry('aneurysms', { type: 'Aneurysm', location: '', size: '', thrombus: false });
  }, [addComplicationEntry]);

  return (
    <div className="form-container">
      <button onClick={onBack} className="back-btn" type="button">
        ← Back to Worksheet Selection
      </button>

      <form onSubmit={hookHandleSubmit}>
        {error && (
          <div className="alert alert-danger" role="alert">
            <strong>Error:</strong> {error}
            <button type="button" className="btn-close" onClick={clearMessages} aria-label="Close" />
          </div>
        )}

        {success && (
          <div className="alert alert-success" role="alert">
            <strong>Success:</strong> {success}
            <button type="button" className="btn-close" onClick={clearMessages} aria-label="Close" />
          </div>
        )}

        {draftBannerProps && <DraftRestoreBanner {...draftBannerProps} />}

        <PatientInfo formData={formData} updateField={updateField} technologists={technologists} fieldErrors={fieldErrors} />

        {/* History */}
        <div className="form-section">
          <h3>History</h3>
          <div className="form-group">
            <label>Clinical History:</label>
            <input
              type="text"
              value={formData.history}
              onChange={(e) => updateField('history', e.target.value)}
              placeholder="Enter clinical history"
            />
          </div>
        </div>

        {/* TAB NAVIGATION */}
        <div className="form-section">
          <div className="worksheet-tabs">
            {TABS.map((tabLabel, tabIndex) => (
              <button
                key={tabLabel}
                type="button"
                onClick={() => setActiveTab(tabIndex)}
                className={`worksheet-tab-btn${activeTab === tabIndex ? ' active' : ''}`}
              >
                {tabLabel}
              </button>
            ))}
          </div>

          <div className="worksheet-tab-content">
            {/* Access & Patency Tab */}
            {activeTab === 0 && (
              <div className="tab-content-grid">
                <h3 style={{ color: 'var(--color-primary)', marginBottom: '20px' }}>Access Identification / Configuration</h3>

                <div className="form-group">
                  <label>Arm:</label>
                  <select
                    value={formData.arm}
                    onChange={(e) => updateField('arm', e.target.value)}
                  >
                    <option value="Right">Right</option>
                    <option value="Left">Left</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Access Type:</label>
                  <select
                    value={formData.accessType}
                    onChange={(e) => updateField('accessType', e.target.value)}
                  >
                    {ACCESS_TYPE_OPTIONS.map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>

                {formData.accessType === 'Synthetic Graft' && (
                  <div className="form-group">
                    <label>Graft Material:</label>
                    <select
                      value={formData.graftMaterial}
                      onChange={(e) => updateField('graftMaterial', e.target.value)}
                    >
                      {GRAFT_MATERIAL_OPTIONS.map(opt => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                    {formData.graftMaterial === 'Other' && (
                      <input
                        type="text"
                        style={{ marginTop: '8px' }}
                        value={formData.graftMaterialOther}
                        onChange={(e) => updateField('graftMaterialOther', e.target.value)}
                        placeholder="Specify material"
                      />
                    )}
                  </div>
                )}

                <div className="form-group">
                  <label>Configuration:</label>
                  <select
                    value={formData.configuration}
                    onChange={(e) => updateField('configuration', e.target.value)}
                  >
                    {CONFIGURATION_OPTIONS.map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                  {formData.configuration === 'Other' && (
                    <input
                      type="text"
                      style={{ marginTop: '8px' }}
                      value={formData.configurationOther}
                      onChange={(e) => updateField('configurationOther', e.target.value)}
                      placeholder="Specify configuration"
                    />
                  )}
                </div>

                <div className="form-group">
                  <label>Indication for Study:</label>
                  <select
                    value={formData.indication}
                    onChange={(e) => updateField('indication', e.target.value)}
                  >
                    {INDICATION_OPTIONS.map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                  {formData.indication === 'Other' && (
                    <input
                      type="text"
                      style={{ marginTop: '8px' }}
                      value={formData.indicationOther}
                      onChange={(e) => updateField('indicationOther', e.target.value)}
                      placeholder="Specify indication"
                    />
                  )}
                </div>

                <h3 style={{ color: 'var(--color-primary)', margin: '25px 0 20px' }}>Patency</h3>

                <div className="form-group">
                  <label>Overall Patency:</label>
                  <select
                    value={formData.patency}
                    onChange={(e) => updateField('patency', e.target.value)}
                  >
                    {PATENCY_OPTIONS.map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {/* Inflow & Anastomosis Tab */}
            {activeTab === 1 && (
              <div className="tab-content-grid">
                <h3 style={{ color: 'var(--color-primary)', marginBottom: '20px' }}>Inflow (Feeding Artery)</h3>

                <div className="form-group">
                  <label>PSV Proximal to Anastomosis (cm/sec):</label>
                  <input
                    type="number"
                    step="0.1"
                    placeholder="Measurement"
                    value={formData.inflow.psv}
                    onChange={(e) => updateNestedField('inflow.psv', e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label>Waveform Character:</label>
                  <select
                    value={formData.inflow.waveform}
                    onChange={(e) => updateNestedField('inflow.waveform', e.target.value)}
                  >
                    {WAVEFORM_OPTIONS.map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.inflow.stenosisPresent}
                      onChange={(e) => updateNestedField('inflow.stenosisPresent', e.target.checked)}
                    />
                    {' '}Focal Stenosis Present
                  </label>
                </div>

                {formData.inflow.stenosisPresent && (
                  <div className="form-group">
                    <label>Stenosis PSV (cm/sec):</label>
                    <input
                      type="number"
                      step="0.1"
                      placeholder="Measurement"
                      value={formData.inflow.stenosisPSV}
                      onChange={(e) => updateNestedField('inflow.stenosisPSV', e.target.value)}
                    />
                  </div>
                )}

                <h3 style={{ color: 'var(--color-primary)', margin: '25px 0 20px' }}>Anastomosis</h3>

                <div className="form-group">
                  <label>PSV at Anastomosis (cm/sec):</label>
                  <input
                    type="number"
                    step="0.1"
                    placeholder="Measurement"
                    value={formData.anastomosis.psv}
                    onChange={(e) => updateNestedField('anastomosis.psv', e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label>Diameter (mm):</label>
                  <input
                    type="number"
                    step="0.1"
                    placeholder="Measurement"
                    value={formData.anastomosis.diameter}
                    onChange={(e) => updateNestedField('anastomosis.diameter', e.target.value)}
                  />
                </div>
              </div>
            )}

            {/* Body & Outflow Tab */}
            {activeTab === 2 && (
              <div className="tab-content-grid">
                <h3 style={{ color: 'var(--color-primary)', marginBottom: '20px' }}>Body of Fistula / Graft</h3>

                <div className="form-group">
                  <label>Proximal Diameter (mm):</label>
                  <input
                    type="number"
                    step="0.1"
                    placeholder="Measurement"
                    value={formData.body.proximalDiameter}
                    onChange={(e) => updateNestedField('body.proximalDiameter', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>Proximal PSV (cm/sec):</label>
                  <input
                    type="number"
                    step="0.1"
                    placeholder="Measurement"
                    value={formData.body.proximalPSV}
                    onChange={(e) => updateNestedField('body.proximalPSV', e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label>Mid Diameter (mm):</label>
                  <input
                    type="number"
                    step="0.1"
                    placeholder="Measurement"
                    value={formData.body.midDiameter}
                    onChange={(e) => updateNestedField('body.midDiameter', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>Mid PSV (cm/sec):</label>
                  <input
                    type="number"
                    step="0.1"
                    placeholder="Measurement"
                    value={formData.body.midPSV}
                    onChange={(e) => updateNestedField('body.midPSV', e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label>Distal Diameter (mm):</label>
                  <input
                    type="number"
                    step="0.1"
                    placeholder="Measurement"
                    value={formData.body.distalDiameter}
                    onChange={(e) => updateNestedField('body.distalDiameter', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>Distal PSV (cm/sec):</label>
                  <input
                    type="number"
                    step="0.1"
                    placeholder="Measurement"
                    value={formData.body.distalPSV}
                    onChange={(e) => updateNestedField('body.distalPSV', e.target.value)}
                  />
                </div>

                <div className="checkbox-group">
                  <label>Wall Characteristics:</label>
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.body.wallThickening}
                      onChange={(e) => updateNestedField('body.wallThickening', e.target.checked)}
                    />
                    {' '}Wall Thickening
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.body.calcification}
                      onChange={(e) => updateNestedField('body.calcification', e.target.checked)}
                    />
                    {' '}Calcification
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.body.muralThrombus}
                      onChange={(e) => updateNestedField('body.muralThrombus', e.target.checked)}
                    />
                    {' '}Mural Thrombus
                  </label>
                </div>

                <h3 style={{ color: 'var(--color-primary)', margin: '25px 0 20px' }}>Outflow Vein</h3>

                <div className="form-group">
                  <label>PSV (cm/sec):</label>
                  <input
                    type="number"
                    step="0.1"
                    placeholder="Measurement"
                    value={formData.outflow.psv}
                    onChange={(e) => updateNestedField('outflow.psv', e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.outflow.stenosisPresent}
                      onChange={(e) => updateNestedField('outflow.stenosisPresent', e.target.checked)}
                    />
                    {' '}Stenosis Present
                  </label>
                </div>

                {formData.outflow.stenosisPresent && (
                  <>
                    <div className="form-group">
                      <label>Stenosis Location:</label>
                      <input
                        type="text"
                        value={formData.outflow.stenosisLocation}
                        onChange={(e) => updateNestedField('outflow.stenosisLocation', e.target.value)}
                        placeholder="e.g., Cephalic arch"
                      />
                    </div>
                    <div className="form-group">
                      <label>Stenosis PSV (cm/sec):</label>
                      <input
                        type="number"
                        step="0.1"
                        placeholder="Measurement"
                        value={formData.outflow.stenosisPSV}
                        onChange={(e) => updateNestedField('outflow.stenosisPSV', e.target.value)}
                      />
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Complications Tab */}
            {activeTab === 3 && (
              <div>
                <h3 style={{ color: 'var(--color-primary)', marginBottom: '20px' }}>Stenosis</h3>
                <button type="button" onClick={addStenosis} className="add-mass-btn">
                  + Add Stenosis
                </button>
                {formData.complications.stenoses.map((s, index) => (
                  <div key={index} className="mass-section">
                    <h4>Stenosis {index + 1}</h4>
                    <div className="form-group">
                      <label>Location:</label>
                      <input
                        type="text"
                        value={s.location}
                        onChange={(e) => updateComplicationEntry('stenoses', index, 'location', e.target.value)}
                        placeholder="e.g., Juxta-anastomotic"
                      />
                    </div>
                    <div className="form-group">
                      <label>Severity (by velocity criteria):</label>
                      <select
                        value={s.severity}
                        onChange={(e) => updateComplicationEntry('stenoses', index, 'severity', e.target.value)}
                      >
                        {STENOSIS_SEVERITY_OPTIONS.map(opt => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Gray-Scale Appearance:</label>
                      <input
                        type="text"
                        value={s.grayscale}
                        onChange={(e) => updateComplicationEntry('stenoses', index, 'grayscale', e.target.value)}
                        placeholder="e.g., Intimal hyperplasia"
                      />
                    </div>
                    <button type="button" onClick={() => removeComplicationEntry('stenoses', index)} className="remove-btn">
                      Remove Stenosis
                    </button>
                  </div>
                ))}

                <h3 style={{ color: 'var(--color-primary)', margin: '25px 0 20px' }}>Thrombus</h3>
                <button type="button" onClick={addThrombus} className="add-mass-btn">
                  + Add Thrombus
                </button>
                {formData.complications.thrombi.map((t, index) => (
                  <div key={index} className="mass-section">
                    <h4>Thrombus {index + 1}</h4>
                    <div className="form-group">
                      <label>Location:</label>
                      <input
                        type="text"
                        value={t.location}
                        onChange={(e) => updateComplicationEntry('thrombi', index, 'location', e.target.value)}
                      />
                    </div>
                    <div className="form-group">
                      <label>Type:</label>
                      <select
                        value={t.occlusive}
                        onChange={(e) => updateComplicationEntry('thrombi', index, 'occlusive', e.target.value)}
                      >
                        <option value="Occlusive">Occlusive</option>
                        <option value="Non-occlusive">Non-occlusive</option>
                      </select>
                    </div>
                    <button type="button" onClick={() => removeComplicationEntry('thrombi', index)} className="remove-btn">
                      Remove Thrombus
                    </button>
                  </div>
                ))}

                <h3 style={{ color: 'var(--color-primary)', margin: '25px 0 20px' }}>Aneurysm / Pseudoaneurysm</h3>
                <button type="button" onClick={addAneurysm} className="add-mass-btn">
                  + Add Aneurysm/Pseudoaneurysm
                </button>
                {formData.complications.aneurysms.map((a, index) => (
                  <div key={index} className="mass-section">
                    <h4>{a.type || 'Aneurysm'} {index + 1}</h4>
                    <div className="form-group">
                      <label>Type:</label>
                      <select
                        value={a.type}
                        onChange={(e) => updateComplicationEntry('aneurysms', index, 'type', e.target.value)}
                      >
                        {ANEURYSM_TYPE_OPTIONS.map(opt => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Location:</label>
                      <input
                        type="text"
                        value={a.location}
                        onChange={(e) => updateComplicationEntry('aneurysms', index, 'location', e.target.value)}
                      />
                    </div>
                    <div className="form-group">
                      <label>Size (cm):</label>
                      <input
                        type="text"
                        value={a.size}
                        onChange={(e) => updateComplicationEntry('aneurysms', index, 'size', e.target.value)}
                        placeholder="e.g., 2.1 x 1.8"
                      />
                    </div>
                    <div className="form-group">
                      <label>
                        <input
                          type="checkbox"
                          checked={a.thrombus}
                          onChange={(e) => updateComplicationEntry('aneurysms', index, 'thrombus', e.target.checked)}
                        />
                        {' '}Contains Thrombus
                      </label>
                    </div>
                    <button type="button" onClick={() => removeComplicationEntry('aneurysms', index)} className="remove-btn">
                      Remove
                    </button>
                  </div>
                ))}

                <h3 style={{ color: 'var(--color-primary)', margin: '25px 0 20px' }}>Perigraft Fluid Collection</h3>
                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.complications.perigraftFluidPresent}
                      onChange={(e) => updateNestedField('complications.perigraftFluidPresent', e.target.checked)}
                    />
                    {' '}Present
                  </label>
                </div>
                {formData.complications.perigraftFluidPresent && (
                  <>
                    <div className="form-group">
                      <label>Type:</label>
                      <select
                        value={formData.complications.perigraftFluidType}
                        onChange={(e) => updateNestedField('complications.perigraftFluidType', e.target.value)}
                      >
                        {PERIGRAFT_FLUID_TYPE_OPTIONS.map(opt => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Description:</label>
                      <input
                        type="text"
                        value={formData.complications.perigraftFluidDescription}
                        onChange={(e) => updateNestedField('complications.perigraftFluidDescription', e.target.value)}
                        placeholder="Location, size, additional detail"
                      />
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Comments */}
        <div className="form-section">
          <h3>Comments</h3>
          <textarea
            rows="8"
            value={formData.comments}
            onChange={(e) => updateField('comments', e.target.value)}
            placeholder="Additional comments..."
            style={{ width: '100%', minHeight: '150px', fontSize: '16px' }}
          />
        </div>

        {/* Form Actions */}
        <div className="form-actions">
          <button type="button" onClick={handlePreview} disabled={isLoading} className="preview-btn">
            Preview Report
          </button>
          <button type="submit" disabled={isLoading} className="submit-btn">
            {isLoading ? 'Generating Report...' : 'Generate & Save Report'}
          </button>
          <button type="button" onClick={resetForm} disabled={isLoading} className="reset-btn">
            Clear Form
          </button>
        </div>
      </form>
    </div>
  );
}

export default DialysisAccessForm;
